import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classnames from 'classnames'
import Grid from '../../../components/svg/grid'
import List from '../../../components/svg/list'
import {
  PriceFilter,
  LeaseFinanceFilter,
  MakeModelFilter,
  MoreFilters,
  YearMileageFilter,
  FeatureFilter,
  LocationFilter,
  ListingTypeFilter,
  SortFilter,
  FilterAccordion,
  BodyStyleFilter,
} from './filters'
import ClickOutsideBoundary from '../../../components/clickoutside-boundary'
import Modal from '../../../components/modal'
import { Desktop, Mobile } from '../../../components/media'
import { Button } from '../../../components/button'
import { zIndex } from '../../../constants'
import FilterHandle from './filter-handle'
import SortHandle from './sort-handle'
import FilterTags from './filter-tags'
import LocationFilterHandle from './location-filter-handle'
import ListingTypeFilterHandle from './listing-type-filter-handle'
import { ListingViewType } from '../config'
import TooltipIcon from '../../../components/svg/tooltip-icon'
import { Tooltip as ReactTooltip } from 'react-tooltip'

const REQUIRED_SIZE_FOR_MORE_BTN = 380

export default memo(function Filters({
  totalCount,
  totalUnrestrictedCount,
  query,
  setPrice,
  setLeaseFinance,
  setMakes,
  setModels,
  setAllModels,
  setBodyStyle,
  setFeature,
  setYear,
  setMileage,
  setFuel,
  setEngineSize,
  setTransmission,
  setDrivetrain,
  setColour,
  setMembership,
  setLocation,
  setDistance,
  setListingType,
  setViewType,
  clearFilters,
  removeTag,
  mouseDownTarget,
  setSortType,
}) {
  const filterTagsRef = useRef()
  const [searchTagsState, setSearchTagsState] = useState({
    maxCountOnFirstRow: 0,
    hasPreviouslyOpened: false,
  })
  const [showActiveFilters, setShowActiveFilters] = useState(false)
  const [openFilters, setOpenFilters] = useState({
    price: false,
    leaseFinance: false,
    makeModel: false,
    bodyStyle: false,
    feature: false,
    yearMileage: false,
    moreFilters: false,
    location: false,
    listingType: false,
    all: false,
    sort: false,
  })

  const totalCountFormatted = useMemo(() => {
    let formattedValue = new Intl.NumberFormat('en-US').format(totalCount || 0)
    if (totalUnrestrictedCount > totalCount) return `> ${formattedValue}`
    return formattedValue
  }, [totalCount, totalUnrestrictedCount])

  const tooltipText = useMemo(() => {
    const totalCountFormatted = new Intl.NumberFormat('en-US').format(totalCount || 0)
    const totalUnrestrictedCountFormatted = new Intl.NumberFormat('en-US').format(
      totalUnrestrictedCount || 0,
    )

    return `Showing ${totalCountFormatted} of ${totalUnrestrictedCountFormatted}. <br /> You can narrow your search results using the filters above.`
  }, [totalCount, totalUnrestrictedCount])

  const shouldShowResultsNotificationIcon = totalUnrestrictedCount > totalCount

  const handleFilterToggle = useCallback((name) => {
    setOpenFilters((prev) => ({ ...prev, [name]: !prev[name] }))
  }, [])

  const priceFilter = (
    <PriceFilter
      price={query.price}
      leaseFinance={query.leaseFinance}
      numberOfCarsInRange={totalCount}
      onChange={setPrice}
      errors={query.errors}
      handleFilterToggle={handleFilterToggle}
    />
  )

  const leaseFinanceFilter = (
    <LeaseFinanceFilter
      leaseFinance={query.leaseFinance}
      numberOfCarsInRange={totalCount}
      onChange={setLeaseFinance}
      errors={query.errors}
      handleFilterToggle={handleFilterToggle}
    />
  )

  const makeModelFilter = (
    <MakeModelFilter
      checkedMakes={query.makes}
      onMakeChange={setMakes}
      checkedModels={query.models}
      onModelChange={setModels}
      onAllModelSet={setAllModels}
      handleFilterToggle={handleFilterToggle}
    />
  )
  const bodyStyleFilter = (
    <BodyStyleFilter
      checkedStyles={query.bodyStyle}
      onChange={setBodyStyle}
      handleFilterToggle={handleFilterToggle}
    />
  )
  const featureFilter = <FeatureFilter selectedFeatures={query.feature} onSelect={setFeature} />
  const yearMileageFilter = (
    <YearMileageFilter
      year={query.year}
      mileage={query.mileage}
      maxMileage={query.maxMileage}
      onYearChange={setYear}
      onMileageChange={setMileage}
      errors={query.errors}
      handleFilterToggle={handleFilterToggle}
    />
  )
  const sortFilter = (
    <SortFilter onSortChange={setSortType} handleFilterToggle={handleFilterToggle} />
  )
  const locationFilter = (
    <LocationFilter
      location={query.location}
      distance={query.distance}
      onLocationChange={setLocation}
      onDistanceChange={setDistance}
      onClose={handleFilterToggle}
      name={'location'}
    />
  )

  const listingTypeFilter = (
    <ListingTypeFilter checkedTypes={query.listingType} onChange={setListingType} />
  )

  const moreFilters = (
    <MoreFilters
      selectedFuels={query.fuel}
      onFuelSelect={setFuel}
      selectedEngineSizes={query.engineSize}
      onEngineSizeSelect={setEngineSize}
      selectedTransmissionTypes={query.transmission}
      onTransmissionTypeSelect={setTransmission}
      selectedDrivetrains={query.drivetrain}
      onDrivetrainChange={setDrivetrain}
      selectedColours={query.colour}
      onColourChange={setColour}
      selectedMemberships={query.membership}
      onMembershipSelect={setMembership}
    />
  )

  useEffect(() => {
    if (!filterTagsRef.current || searchTagsState.hasPreviouslyOpened) return

    for (let index = 0; index < filterTagsRef.current.children.length; index++) {
      const element = filterTagsRef.current.children[index]
      const isSearchTag = element.tagName.toLowerCase() === 'div'
      if (
        isSearchTag &&
        window.innerWidth - element.offsetLeft + element.offsetWidth < REQUIRED_SIZE_FOR_MORE_BTN
      ) {
        setSearchTagsState((prev) => ({ ...prev, maxCountOnFirstRow: index }))
        return
      }
    }
  }, [query, searchTagsState.hasPreviouslyOpened])

  const onDismiss = (e) => {
    e.stopPropagation()
    setSortType({ fieldName: 'auctionTime', sortOrder: 'Ascending' })
  }

  return (
    <div className='searchFilters'>
      <div className='filterHandlesContainer'>
        <div className='handle yearMileage'>
          <FilterHandle name='yearMileage' onClick={handleFilterToggle}>
            Year & Mileage
          </FilterHandle>
          {openFilters.yearMileage && (
            <>
              <Desktop>
                <ClickOutsideBoundary
                  className='filterBoundary'
                  name='yearMileage'
                  onClose={handleFilterToggle}
                  mouseDownTarget={mouseDownTarget}
                >
                  {yearMileageFilter}
                </ClickOutsideBoundary>
              </Desktop>
              <Mobile>
                <Modal name='yearMileage' onClose={handleFilterToggle}>
                  {yearMileageFilter}
                </Modal>
              </Mobile>
            </>
          )}
        </div>
        <div className='handle makeModel'>
          <FilterHandle name='makeModel' onClick={handleFilterToggle}>
            Make & Model
          </FilterHandle>
          {openFilters.makeModel && (
            <>
              <Desktop>
                <ClickOutsideBoundary
                  className='filterBoundary'
                  name='makeModel'
                  onClose={handleFilterToggle}
                  mouseDownTarget={mouseDownTarget}
                >
                  {makeModelFilter}
                </ClickOutsideBoundary>
              </Desktop>
              <Mobile>
                <Modal name='makeModel' onClose={handleFilterToggle}>
                  {makeModelFilter}
                </Modal>
              </Mobile>
            </>
          )}
        </div>
        <div className='handle price'>
          <FilterHandle name='price' onClick={handleFilterToggle}>
            Price
          </FilterHandle>
          {openFilters.price && (
            <>
              <Desktop>
                <ClickOutsideBoundary
                  className='filterBoundary'
                  name='price'
                  onClose={handleFilterToggle}
                  mouseDownTarget={mouseDownTarget}
                >
                  {priceFilter}
                </ClickOutsideBoundary>
              </Desktop>
              <Mobile>
                <Modal name='price' onClose={handleFilterToggle}>
                  {priceFilter}
                </Modal>
              </Mobile>
            </>
          )}
        </div>
        <div className='handle leaseFinance'>
          <FilterHandle name='leaseFinance' onClick={handleFilterToggle}>
            Payments
          </FilterHandle>
          {openFilters.leaseFinance && (
            <>
              <Desktop>
                <ClickOutsideBoundary
                  className='filterBoundary'
                  name='leaseFinance'
                  onClose={handleFilterToggle}
                  mouseDownTarget={mouseDownTarget}
                >
                  {leaseFinanceFilter}
                </ClickOutsideBoundary>
              </Desktop>
              <Mobile>
                <Modal name='leaseFinance' onClose={handleFilterToggle}>
                  {leaseFinanceFilter}
                </Modal>
              </Mobile>
            </>
          )}
        </div>
        <div className='handle bodyStyle'>
          <FilterHandle name='bodyStyle' onClick={handleFilterToggle}>
            Body style
          </FilterHandle>
          {openFilters.bodyStyle && (
            <>
              <Desktop>
                <ClickOutsideBoundary
                  className='filterBoundary'
                  name='bodyStyle'
                  onClose={handleFilterToggle}
                  mouseDownTarget={mouseDownTarget}
                >
                  {bodyStyleFilter}
                </ClickOutsideBoundary>
              </Desktop>
              <Mobile>
                <Modal name='bodyStyle' onClose={handleFilterToggle}>
                  {bodyStyleFilter}
                </Modal>
              </Mobile>
            </>
          )}
        </div>
        <Desktop>
          <div className='handle feature'>
            <FilterHandle name='feature' onClick={handleFilterToggle}>
              Features
            </FilterHandle>
            {openFilters.feature && (
              <ClickOutsideBoundary
                className='filterBoundary'
                name='feature'
                onClose={handleFilterToggle}
                mouseDownTarget={mouseDownTarget}
              >
                {featureFilter}
              </ClickOutsideBoundary>
            )}
          </div>
        </Desktop>
        <Desktop>
          <div className='handle yearMileage'>
            <FilterHandle name='moreFilters' onClick={handleFilterToggle}>
              More Filters
            </FilterHandle>
            {openFilters.moreFilters && (
              <Modal name='moreFilters' onClose={handleFilterToggle} applyBtn>
                {moreFilters}
              </Modal>
            )}
          </div>
        </Desktop>
        <Mobile>
          <div className='handle all'>
            <FilterHandle name='all' onClick={handleFilterToggle}>
              All
            </FilterHandle>
          </div>
          {openFilters.all && (
            <Modal name='all' onClose={handleFilterToggle}>
              <div className='allFiltersModalWrapper'>
                <div className='allFilterWrapper'>
                  <FilterAccordion
                    open={openFilters.feature}
                    label='Features'
                    name='feature'
                    onToggle={handleFilterToggle}
                  >
                    {featureFilter}
                  </FilterAccordion>
                  {moreFilters}
                </div>
                <Button className='floatingCloseBtn' onClick={() => handleFilterToggle('all')}>
                  Apply
                </Button>
              </div>
            </Modal>
          )}
        </Mobile>
      </div>
      <div className='filterTagsContainer flex alignCenter justifyCenter' ref={filterTagsRef}>
        <FilterTags
          visible={showActiveFilters}
          onToggle={setShowActiveFilters}
          values={query}
          onTagRemove={removeTag}
          onClearFilters={clearFilters}
          searchTagsState={searchTagsState}
          setSearchTagsState={setSearchTagsState}
        />
      </div>

      <div className='viewTypeContainer flex alignCenter justifyBetween'>
        <div className='flex alignCenter'>
          <Desktop>
            <div className='viewType flex alignCenter'>
              <button
                aria-label='grid view'
                className='flatBtn'
                onClick={() => setViewType(ListingViewType.Grid)}
              >
                <Grid className={classnames({ active: query.viewType === ListingViewType.Grid })} />
              </button>
              <button
                aria-label='list view'
                className='flatBtn'
                onClick={() => setViewType(ListingViewType.List)}
              >
                <List className={classnames({ active: query.viewType === ListingViewType.List })} />
              </button>
            </div>
          </Desktop>
          <div className='location'>
            <LocationFilterHandle
              name='location'
              zipCode={query.location.zipCode}
              onClick={handleFilterToggle}
            />
            {openFilters.location && (
              <>
                <Desktop>
                  <Modal
                    className='filterBoundary'
                    name='location'
                    onClose={handleFilterToggle}
                    mouseDownTarget={mouseDownTarget}
                  >
                    {locationFilter}
                  </Modal>
                </Desktop>
                <Mobile>
                  <Modal name='location' onClose={handleFilterToggle}>
                    {locationFilter}
                    <Button
                      className='locationFilterBtn'
                      onClick={() => handleFilterToggle('location')}
                    >
                      Search
                    </Button>
                  </Modal>
                </Mobile>
              </>
            )}
          </div>
          <div className='listingType'>
            <ListingTypeFilterHandle
              name='listingType'
              listingType={query.listingType}
              onClick={handleFilterToggle}
            />
            {openFilters.listingType && (
              <>
                <Desktop>
                  <ClickOutsideBoundary
                    className='filterBoundary'
                    name='listingType'
                    onClose={handleFilterToggle}
                    mouseDownTarget={mouseDownTarget}
                  >
                    {listingTypeFilter}
                  </ClickOutsideBoundary>
                </Desktop>
                <Mobile>
                  <Modal name='listingType' onClose={handleFilterToggle}>
                    {listingTypeFilter}
                    <Button
                      className='listingTypeFilterBtn'
                      onClick={() => handleFilterToggle('listingType')}
                    >
                      Search
                    </Button>
                  </Modal>
                </Mobile>
              </>
            )}
          </div>
        </div>

        <div className='wrapper-sortHandle-count'>
          <div className='flex alignCenter'>
            <span className='sort-label'>Sort By</span>

            <div style={{ position: 'relative' }}>
              <SortHandle
                name='sort'
                onClick={handleFilterToggle}
                query={query}
                onDismiss={onDismiss}
              ></SortHandle>

              {openFilters.sort && (
                <>
                  <Desktop>
                    <ClickOutsideBoundary
                      className='filterBoundary sortBoundary'
                      name='sort'
                      onClose={handleFilterToggle}
                      mouseDownTarget={mouseDownTarget}
                    >
                      {sortFilter}
                    </ClickOutsideBoundary>
                  </Desktop>
                  <Mobile>
                    <Modal name='sort' onClose={handleFilterToggle}>
                      {sortFilter}
                    </Modal>
                  </Mobile>
                </>
              )}
            </div>
          </div>
          <strong className='totalCount'>
            <span>{totalCountFormatted}</span> Results
            {shouldShowResultsNotificationIcon && (
              <div>
                <div
                  className='flex alignCenter'
                  data-tip={tooltipText}
                  data-for='search-result-filter-notification'
                >
                  <TooltipIcon width={20} height={20} />
                </div>
                <ReactTooltip
                  id='search-result-filter-notification'
                  place='right'
                  effect='solid'
                  type='dark'
                  multiline={true}
                />
              </div>
            )}
          </strong>
        </div>
      </div>

      <style jsx>{`
        .allFilterWrapper {
          margin-top: 13rem;
        }
        .wrapper-sortHandle-count {
          display: flex;
          flex-wrap: wrap;
          column-gap: 4rem;
          row-gap: 2rem;
        }
        .sort-label {
          padding-right: 1rem;
          text-align: left;
          color: var(--primary-bg-color);
          font-size: 1.6rem;
          font-weight: 700;
        }

        .searchFilters {
          :global(.filterBoundary) {
            position: absolute;
            width: 43.6rem;
            padding: 0.7rem 0 0;
            top: 100%;
            left: -50%;
          }
          :global(.sortBoundary) {
            width: fit-content;
            left: 0%;
          }

          .feature {
            :global(.filterBoundary) {
              width: 88.5rem;
              left: -160%;
            }
          }

          .location,
          .listingType {
            position: relative;
            margin: 0 0 0 4rem;

            :global(.filterBoundary) {
              left: 0;
            }
          }
        }

        :global(.locationFilterBtn),
        :global(.listingTypeFilterBtn) {
          position: fixed;
          left: 1.3rem;
          bottom: 2.1rem;
          width: calc(100vw - 2.6rem);
        }

        .filterHandlesContainer {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          column-gap: 1rem;
          row-gap: 1rem;

          padding: 3rem 10.4rem 2.4rem;
          background: var(--primary-bg-color);
        }

        .handle {
          position: relative;
          max-width: 21.5rem;
          width: 100%;

          &.all {
            max-width: 5.3rem;

            :global(button) {
              background: var(--primary-button-bg);
              border-color: var(--primary-color);
              color: #fff;
            }
          }
        }

        .allFiltersModalWrapper {
          padding: 0 0 8rem;
          margin-top: 2rem;

          :global(.floatingCloseBtn) {
            position: fixed;
            top: 12rem;
            left: calc(50% - 46.9px);
          }
        }

        .filterTagsContainer {
          width: 100%;
          min-height: 9.7rem;
          padding: 1rem;
          flex-wrap: wrap;
          background: var(--secondary-input-bg);

          :global(.filterTag) {
            margin: 0.5rem 0.6rem;
          }
        }

        .viewTypeContainer {
          max-width: 192rem;
          width: 100%;
          margin: 0 auto;
          padding: 0 4rem;
          flex-wrap: wrap;
          column-gap: 5rem;
          row-gap: 2rem;
          background: var(--secondary-input-bg);

          .viewType {
            button {
              outline: none;

              &:focus {
                :global(svg) {
                  fill: var(--primary-color);
                }
              }
            }

            :global(svg) {
              width: 2.3rem;
              height: 2.3rem;
              margin: 0 1.2rem 0 0;
              fill: var(--primary-bg-color);
            }

            :global(svg.active) {
              fill: var(--primary-color);
            }
          }

          .totalCount {
            display: flex;
            align-items: center;
            gap: 1rem;
            color: var(--primary-bg-color);
            font-size: 1.8rem;
            align-self: center;

            span {
              color: var(--primary-color);
            }
          }
        }

        @media (max-width: 1024px) {
          .searchFilters {
            .location {
              margin: 0;
            }
          }

          .viewTypeContainer {
            .totalCount {
              width: 100%;
              text-align: center;
              font-size: 1.2rem;
            }
          }
          .sort-label {
            font-size: 1.2rem;
          }

          .filterHandlesContainer {
            padding: 3rem 1.3rem 2rem;
            column-gap: 0.5rem;
            row-gap: 0.5rem;
          }

          .viewTypeContainer {
            padding: 2rem 1.2rem;
          }

          .filterTagsContainer {
            min-height: 3rem;
            padding: 0 1.3rem;
            background: #fff;
          }

          .wrapper-sortHandle-count {
            width: 100%;
          }
        }

        @media (max-width: 475px) {
          .filterHandlesContainer {
            flex-direction: column;
            align-items: center;
          }

          .handle {
            &.all {
              max-width: 21.3rem;
            }
          }
        }

        @media (max-width: 388px) {
          .handle {
            margin-left: 0;
          }
        }
      `}</style>

      <style jsx>{`
        .allFiltersModalWrapper {
          :global(.floatingCloseBtn) {
            z-index: ${zIndex.Fixed};
          }
        }

        :global(.filterBoundary) {
          z-index: ${zIndex.Absolute};
        }
      `}</style>
    </div>
  )
})
